import React, { useState, useEffect } from 'react';
import { X, Trash2, Copy, Sliders, Plus, CheckCircle, Info } from 'lucide-react';
import { useWorkflowStore } from '../../hooks/useWorkflowStore';
import api from '../../services/api';

const CONDITION_FIELDS = [
  { id: 'score', label: 'Lead Score (score)', type: 'number' },
  { id: 'tier', label: 'Tier Level (tier)', type: 'string' },
  { id: 'status', label: 'Lead Status (status)', type: 'string', choices: ['new', 'sold', 'no_winner'] },
  { id: 'revenue', label: 'Revenue ($) (revenue)', type: 'number' },
  { id: 'winning_buyer_id', label: 'Winning Buyer ID (winning_buyer_id)', type: 'number' },
  { id: 'state', label: 'State (state)', type: 'string' },
];

const OPERATORS = {
  number: [
    { value: 'gt', label: 'Greater Than (>)' },
    { value: 'gte', label: 'Greater or Equal (>=)' },
    { value: 'lt', label: 'Less Than (<)' },
    { value: 'lte', label: 'Less or Equal (<=)' },
    { value: 'equals', label: 'Equals (=)' },
    { value: 'not_equals', label: 'Not Equals (!=)' },
    { value: 'is_null', label: 'Is Null' },
    { value: 'is_not_null', label: 'Is Not Null' },
  ],
  string: [
    { value: 'equals', label: 'Equals (=)' },
    { value: 'not_equals', label: 'Not Equals (!=)' },
    { value: 'in', label: 'In List' },
  ],
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  background: 'var(--bg-raised)',
  border: '1px solid var(--border)',
  borderRadius: '8px',
  color: 'var(--text-primary)',
  fontSize: '13px',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.2s, box-shadow 0.2s',
};

const selectStyle = {
  ...inputStyle,
  cursor: 'pointer',
  appearance: 'none',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394A3B8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 10px center',
  paddingRight: '32px',
};

export default function NodeConfigDrawer({ campaignId }) {
  const selectedNodeId = useWorkflowStore((state) => state.selectedNodeId);
  const setSelectedNodeId = useWorkflowStore((state) => state.setSelectedNodeId);
  const nodes = useWorkflowStore((state) => state.nodes);
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const duplicateNode = useWorkflowStore((state) => state.duplicateNode);

  const [buyerSets, setBuyerSets] = useState([]);
  const [verticalTiers, setVerticalTiers] = useState([]);

  useEffect(() => {
    const endpoint = campaignId ? `/campaigns/${campaignId}/flow/buyer-sets` : '/buyer-sets';
    api.get(endpoint)
      .then((res) => {
        const items = res.data?.buyer_sets || (Array.isArray(res.data) ? res.data : (res.data?.items || []));
        setBuyerSets(items);
      })
      .catch(() => setBuyerSets([]));
  }, [campaignId]);

  useEffect(() => {
    if (!campaignId) return;
    api.get(`/campaigns/${campaignId}/flow/tiers`)
      .then((res) => {
        setVerticalTiers(res.data?.tiers || []);
      })
      .catch(() => setVerticalTiers([]));
  }, [campaignId]);

  const selectedNode = nodes.find((n) => n.id === selectedNodeId);

  useEffect(() => {
    if (selectedNode?.type === 'route' && buyerSets.length > 0) {
      const validIds = buyerSets.map((b) => b.id);
      const currentId = Number(selectedNode.data?.buyer_set_id);
      if (!currentId || !validIds.includes(currentId)) {
        updateNodeData(selectedNodeId, { buyer_set_id: buyerSets[0].id });
      }
    }
  }, [selectedNode, selectedNodeId, buyerSets, updateNodeData]);

  if (!selectedNode) return null;

  const data = selectedNode.data || {};
  const isStart = selectedNode.type === 'start';

  const handleFieldChange = (key, val) => {
    updateNodeData(selectedNodeId, { [key]: val });
  };

  const handleAddCase = () => {
    const currentCases = data.cases || [];
    const newCase = {
      case_id: `Case Rule ${currentCases.length + 1}`,
      operator: 'gt',
      value: '50',
      label: `Case Rule ${currentCases.length + 1}`,
    };
    handleFieldChange('cases', [...currentCases, newCase]);
  };

  const handleUpdateCase = (index, key, value) => {
    const currentCases = [...(data.cases || [])];
    currentCases[index] = { ...currentCases[index], [key]: value };
    handleFieldChange('cases', currentCases);
  };

  const handleRemoveCase = (index) => {
    const currentCases = [...(data.cases || [])];
    currentCases.splice(index, 1);
    handleFieldChange('cases', currentCases);
  };

  const handleAddBranch = () => {
    const currentBranches = data.branches || [];
    const newCount = currentBranches.length + 1;
    const newBranch = {
      branch_id: `variant_${Date.now().toString().slice(-4)}`,
      weight: 10,
      label: `Variant ${String.fromCharCode(64 + newCount)} (10%)`,
    };
    handleFieldChange('branches', [...currentBranches, newBranch]);
  };

  const handleUpdateBranch = (index, key, value) => {
    const currentBranches = [...(data.branches || [])];
    currentBranches[index] = { ...currentBranches[index], [key]: value };
    handleFieldChange('branches', currentBranches);
  };

  const handleRemoveBranch = (index) => {
    const currentBranches = [...(data.branches || [])];
    currentBranches.splice(index, 1);
    handleFieldChange('branches', currentBranches);
  };

  const handleAutoBalanceBranches = () => {
    const currentBranches = data.branches || [];
    if (currentBranches.length === 0) return;
    const equalShare = Math.floor(100 / currentBranches.length);
    const remainder = 100 - equalShare * currentBranches.length;

    const balanced = currentBranches.map((b, idx) => ({
      ...b,
      weight: equalShare + (idx === 0 ? remainder : 0),
    }));
    handleFieldChange('branches', balanced);
  };

  const selectedFieldSpec = CONDITION_FIELDS.find((f) => f.id === (data.field || 'score')) || CONDITION_FIELDS[0];
  const availableOps = OPERATORS[selectedFieldSpec.type] || OPERATORS.number;

  return (
    <aside
      className="node-config-drawer"
      style={{
        width: 360,
        background: 'var(--bg-surface)',
        borderLeft: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        zIndex: 10,
        boxShadow: 'var(--shadow-lg)',
        userSelect: 'none',
      }}
    >
      {/* Drawer Header */}
      <div
        style={{
          padding: '14px 18px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(0,0,0,0.02)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              background: 'var(--accent-soft)',
              color: 'var(--accent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Sliders size={16} />
          </div>
          <div>
            <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0, lineHeight: 1.2 }}>
              Configure Node
            </h3>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
              Type: {selectedNode.type}
            </span>
          </div>
        </div>

        <button
          className="btn btn-ghost btn-icon"
          style={{ width: 28, height: 28, padding: 0 }}
          onClick={() => setSelectedNodeId(null)}
          title="Close Drawer"
        >
          <X size={16} />
        </button>
      </div>

      {/* Drawer Body */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '18px',
          display: 'flex',
          flexDirection: 'column',
          gap: 18,
        }}
      >
        {/* Node Name */}
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>
            Node Label / Name
          </label>
          <input
            type="text"
            className="input"
            style={inputStyle}
            value={data.label || data.title || ''}
            onChange={(e) => handleFieldChange('label', e.target.value)}
            placeholder="e.g. Lead Score Threshold"
          />
        </div>

        {/* Description */}
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>
            Description
          </label>
          <input
            type="text"
            className="input"
            style={inputStyle}
            value={data.subtitle || ''}
            onChange={(e) => handleFieldChange('subtitle', e.target.value)}
            placeholder="Brief node subtitle notes..."
          />
        </div>

        <hr style={{ borderColor: 'var(--border)', margin: '2px 0' }} />

        {/* Route Node Settings */}
        {selectedNode.type === 'route' && (
          <>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>
                Routing Engine
              </label>
              <select
                className="select"
                style={selectStyle}
                value={data.engine || 'waterfall'}
                onChange={(e) => handleFieldChange('engine', e.target.value)}
              >
                <option value="waterfall">Waterfall (Sequential Tier Matching)</option>
                <option value="auction">Auction (Highest Bidder Ping/Post)</option>
                <option value="direct">Direct (Single Buyer Assignment)</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>
                Target Buyer Set
              </label>
              {buyerSets.length > 0 ? (
                <select
                  className="select"
                  style={selectStyle}
                  value={data.buyer_set_id || buyerSets[0]?.id || 1}
                  onChange={(e) => handleFieldChange('buyer_set_id', Number(e.target.value))}
                >
                  {buyerSets.map((b) => (
                    <option key={b.id} value={b.id}>
                      #{b.id} - {b.name} ({b.engine || 'set'})
                    </option>
                  ))}
                </select>
              ) : (
                <div style={{ background: 'rgba(245, 158, 11, 0.15)', border: '1px solid rgba(245, 158, 11, 0.4)', borderRadius: 8, padding: '8px 12px', fontSize: 11, color: '#FBBF24', lineHeight: 1.4 }}>
                  ⚠️ No active Buyer Sets found for this campaign vertical. Create a Buyer Set in settings before publishing.
                </div>
              )}
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>
                Fixed Price ($) — Optional
              </label>
              <input
                type="number"
                step="0.01"
                className="input"
                style={inputStyle}
                value={data.fixed_price || ''}
                onChange={(e) =>
                  handleFieldChange(
                    'fixed_price',
                    e.target.value ? Number(e.target.value) : null
                  )
                }
                placeholder="e.g. 25.00"
              />
            </div>
          </>
        )}

        {/* Condition Node Settings */}
        {selectedNode.type === 'condition' && (
          <>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', color: 'var(--text-muted)', textTransform: 'uppercase', margin: 0 }}>
                  Evaluation Field
                </label>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: 99,
                    background: selectedFieldSpec.type === 'number' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(168, 85, 247, 0.15)',
                    color: selectedFieldSpec.type === 'number' ? '#60A5FA' : '#C084FC',
                  }}
                >
                  {selectedFieldSpec.type === 'number' ? '🔢 Numerical Math' : '🔤 Text String'}
                </span>
              </div>
              <select
                className="select"
                style={selectStyle}
                value={data.field || 'score'}
                onChange={(e) => {
                  const newField = e.target.value;
                  const newSpec = CONDITION_FIELDS.find((f) => f.id === newField) || CONDITION_FIELDS[0];
                  const newAvailableOps = OPERATORS[newSpec.type] || OPERATORS.number;

                  // Update field and sanitize operators in cases
                  const updatedCases = (data.cases || []).map((c) => {
                    const validOpValues = newAvailableOps.map((op) => op.value);
                    const validOp = validOpValues.includes(c.operator) ? c.operator : newAvailableOps[0].value;
                    return { ...c, operator: validOp };
                  });

                  updateNodeData(selectedNodeId, { field: newField, cases: updatedCases });
                }}
              >
                {CONDITION_FIELDS.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 10,
                }}
              >
                <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', color: 'var(--text-muted)', textTransform: 'uppercase', margin: 0 }}>
                  Rule Cases & Output Handles
                </label>
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ fontSize: 11, padding: '3px 10px', height: 26 }}
                  onClick={handleAddCase}
                >
                  <Plus size={12} /> Add Case
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {(data.cases || []).map((c, idx) => {
                  const isNullOp = c.operator === 'is_null' || c.operator === 'is_not_null';
                  const isStatusField = selectedFieldSpec.id === 'status';
                  const isDuplicateCaseId = (data.cases || []).some(
                    (other, otherIdx) => otherIdx !== idx && other.case_id && other.case_id.trim().toLowerCase() === (c.case_id || '').trim().toLowerCase()
                  );

                  return (
                    <div
                      key={idx}
                      style={{
                        padding: 12,
                        borderRadius: 10,
                        background: 'var(--bg-raised)',
                        border: isDuplicateCaseId ? '1px solid var(--amber)' : '1px solid var(--border)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 10,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)' }}>
                          Case Rule #{idx + 1}
                        </span>
                        <button
                          className="btn btn-ghost btn-icon"
                          style={{ color: 'var(--red)', width: 22, height: 22, padding: 0 }}
                          onClick={() => handleRemoveCase(idx)}
                          title="Remove Case"
                        >
                          <X size={14} />
                        </button>
                      </div>

                      <div>
                        <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
                          Branch Label / Case Name (supports spaces & multi-words)
                        </span>
                        <input
                          type="text"
                          className="input"
                          style={{
                            ...inputStyle,
                            padding: '6px 10px',
                            fontSize: 12,
                            borderColor: isDuplicateCaseId ? 'var(--amber)' : undefined,
                          }}
                          value={c.case_id || ''}
                          onChange={(e) => handleUpdateCase(idx, 'case_id', e.target.value)}
                          placeholder="e.g. High Score, Tier A, VIP Lead"
                        />
                        {isDuplicateCaseId && (
                          <span style={{ fontSize: 10, color: '#FBBF24', marginTop: 4, display: 'block', fontWeight: 600 }}>
                            ⚠️ Duplicate branch name in this node! Add a suffix (e.g. {c.case_id} #2)
                          </span>
                        )}
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div>
                          <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
                            Operator
                          </span>
                          <select
                            className="select"
                            style={{ ...selectStyle, padding: '6px 10px', fontSize: 12 }}
                            value={c.operator || availableOps[0].value}
                            onChange={(e) => handleUpdateCase(idx, 'operator', e.target.value)}
                          >
                            {availableOps.map((op) => (
                              <option key={op.value} value={op.value}>
                                {op.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
                            Target Value
                          </span>
                          {isNullOp ? (
                            <input
                              type="text"
                              className="input"
                              disabled
                              style={{ ...inputStyle, padding: '6px 10px', fontSize: 12, opacity: 0.5 }}
                              value="(None)"
                            />
                          ) : isStatusField ? (
                            <select
                              className="select"
                              style={{ ...selectStyle, padding: '6px 10px', fontSize: 12 }}
                              value={c.value || 'sold'}
                              onChange={(e) => handleUpdateCase(idx, 'value', e.target.value)}
                            >
                              <option value="new">new</option>
                              <option value="sold">sold</option>
                              <option value="no_winner">no_winner</option>
                            </select>
                          ) : selectedFieldSpec.id === 'tier' && verticalTiers.length > 0 ? (
                            <select
                              className="select"
                              style={{ ...selectStyle, padding: '6px 10px', fontSize: 12 }}
                              value={c.value || verticalTiers[0]?.tier || ''}
                              onChange={(e) => handleUpdateCase(idx, 'value', e.target.value)}
                            >
                              {verticalTiers.map((t) => (
                                <option key={t.tier} value={t.tier}>
                                  {t.label}
                                </option>
                              ))}
                            </select>
                          ) : selectedFieldSpec.type === 'number' ? (
                            <input
                              type="number"
                              className="input"
                              style={{ ...inputStyle, padding: '6px 10px', fontSize: 12 }}
                              value={c.value ?? ''}
                              onChange={(e) =>
                                handleUpdateCase(
                                  idx,
                                  'value',
                                  e.target.value ? Number(e.target.value) : ''
                                )
                              }
                              placeholder="e.g. 50"
                            />
                          ) : (
                            <input
                              type="text"
                              className="input"
                              style={{ ...inputStyle, padding: '6px 10px', fontSize: 12 }}
                              value={c.value ?? ''}
                              onChange={(e) => handleUpdateCase(idx, 'value', e.target.value)}
                              placeholder="e.g. TIER_1"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div
                style={{
                  marginTop: 12,
                  fontSize: 11,
                  color: 'var(--text-secondary)',
                  background: 'var(--bg-base)',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  lineHeight: 1.4,
                }}
              >
                <Info size={16} style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 2 }} />
                <span>
                  Condition nodes automatically maintain a mandatory <strong>false (else)</strong> fallback handle for unmatched leads.
                </span>
              </div>
            </div>
          </>
        )}

        {/* Probability Node Settings */}
        {selectedNode.type === 'probability' && (
          <>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', color: 'var(--text-muted)', textTransform: 'uppercase', margin: 0 }}>
                  Traffic Split Branches
                </label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    className="btn btn-secondary btn-sm"
                    style={{ fontSize: 10, padding: '2px 8px', height: 24, color: '#EC4899', borderColor: 'rgba(236, 72, 153, 0.4)' }}
                    onClick={handleAutoBalanceBranches}
                    title="Distribute 100% weight evenly across all branches"
                  >
                    Auto-Balance
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    style={{ fontSize: 10, padding: '2px 8px', height: 24 }}
                    onClick={handleAddBranch}
                  >
                    <Plus size={12} /> Add Branch
                  </button>
                </div>
              </div>

              {/* Total Weight Indicator Bar */}
              {(() => {
                const totalWeight = (data.branches || []).reduce((acc, b) => acc + (Number(b.weight) || 0), 0);
                const isBalanced = Math.abs(totalWeight - 100) < 0.01;
                return (
                  <div style={{ marginBottom: 14, background: 'var(--bg-raised)', padding: 12, borderRadius: 8, border: `1px solid ${isBalanced ? 'rgba(16, 185, 129, 0.4)' : 'rgba(245, 158, 11, 0.5)'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontWeight: 700, marginBottom: 6 }}>
                      <span>Total Distribution Weight</span>
                      <span style={{ color: isBalanced ? '#10B981' : '#FBBF24' }}>
                        {totalWeight}% {isBalanced ? '(100% Balanced)' : '(Must equal 100%)'}
                      </span>
                    </div>
                    <div style={{ width: '100%', height: 6, borderRadius: 99, background: 'var(--bg-surface)', overflow: 'hidden' }}>
                      <div
                        style={{
                          width: `${Math.min(totalWeight, 100)}%`,
                          height: '100%',
                          background: isBalanced ? '#10B981' : '#FBBF24',
                          transition: 'all 0.3s ease',
                        }}
                      />
                    </div>
                  </div>
                );
              })()}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {(data.branches || []).map((b, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: 12,
                      borderRadius: 10,
                      background: 'var(--bg-raised)',
                      border: '1px solid var(--border)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#F472B6' }}>
                        Branch #{idx + 1} ({b.weight || 0}%)
                      </span>
                      <button
                        className="btn btn-ghost btn-icon"
                        style={{ color: 'var(--red)', width: 22, height: 22, padding: 0 }}
                        onClick={() => handleRemoveBranch(idx)}
                        title="Remove Branch"
                      >
                        <X size={14} />
                      </button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8 }}>
                      <div>
                        <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
                          Branch Label
                        </span>
                        <input
                          type="text"
                          className="input"
                          style={{ ...inputStyle, padding: '6px 10px', fontSize: 12 }}
                          value={b.label || b.branch_id || ''}
                          onChange={(e) => {
                            handleUpdateBranch(idx, 'label', e.target.value);
                            handleUpdateBranch(idx, 'branch_id', e.target.value);
                          }}
                          placeholder="e.g. Variant A (30%)"
                        />
                      </div>

                      <div>
                        <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
                          Chance %
                        </span>
                        <input
                          type="number"
                          min="1"
                          max="100"
                          className="input"
                          style={{ ...inputStyle, padding: '6px 10px', fontSize: 12 }}
                          value={b.weight ?? 0}
                          onChange={(e) => handleUpdateBranch(idx, 'weight', Number(e.target.value))}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div
                style={{
                  marginTop: 12,
                  fontSize: 11,
                  color: 'var(--text-secondary)',
                  background: 'var(--bg-base)',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  lineHeight: 1.4,
                }}
              >
                <Info size={16} style={{ color: '#EC4899', flexShrink: 0, marginTop: 2 }} />
                <span>
                  Traffic is randomly routed to each output handle based on its assigned percentage weight.
                </span>
              </div>
            </div>
          </>
        )}

        {/* Offerwall Node Settings */}
        {selectedNode.type === 'offerwall' && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>
                  Offerwall Redirect URL *
                </label>
                <input
                  className="input"
                  type="url"
                  placeholder="https://offers.kickoffadvertising.com/wall?lead={lead_id}&sub={sub_id}"
                  value={data.redirect_url || ''}
                  onChange={(e) => handleFieldChange('redirect_url', e.target.value)}
                  style={{ width: '100%', fontFamily: 'monospace', fontSize: 12 }}
                />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.4 }}>
                  Available dynamic tags: <code style={{ color: 'var(--accent)' }}>{'{lead_id}'}</code> (or <code style={{ color: 'var(--accent)' }}>{'{kickoff_lead_id}'}</code>), <code style={{ color: 'var(--accent)' }}>{'{aff_id}'}</code> (or <code style={{ color: 'var(--accent)' }}>{'{affiliate_id}'}</code>), <code style={{ color: 'var(--accent)' }}>{'{sub_id}'}</code>, <code style={{ color: 'var(--accent)' }}>{'{score}'}</code>, <code style={{ color: 'var(--accent)' }}>{'{state}'}</code>, <code style={{ color: 'var(--accent)' }}>{'{zip_code}'}</code>
                </div>
              </div>

              <div>
                <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>
                  Estimated Payout / CPC Revenue ($)
                </label>
                <input
                  className="input"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="2.50"
                  value={data.payout ?? ''}
                  onChange={(e) => handleFieldChange('payout', e.target.value === '' ? '' : Number(e.target.value))}
                  style={{ width: '100%' }}
                />

                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  Recorded on the lead as revenue when redirected to this offerwall.
                </div>
              </div>
            </div>
          </>
        )}

      </div>

      {/* Drawer Footer */}
      {!isStart && (
        <div
          style={{
            padding: '14px 18px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            gap: 10,
            background: 'var(--bg-base)',
          }}
        >
          <button
            className="btn btn-secondary"
            style={{ flex: 1, fontSize: 12 }}
            onClick={() => duplicateNode(selectedNodeId)}
          >
            <Copy size={14} /> Duplicate Node
          </button>
          <button
            className="btn btn-secondary"
            style={{ color: 'var(--red)', borderColor: 'var(--red-bg)', fontSize: 12 }}
            onClick={() => {
              deleteNode(selectedNodeId);
              setSelectedNodeId(null);
            }}
          >
            <Trash2 size={14} /> Delete
          </button>
        </div>
      )}
    </aside>
  );
}
